import { useState } from "react";
import { Trophy, Award, Play, Keyboard, Gem, CalendarDays, BookOpen } from "lucide-react";
import { DIFFICULTIES, BESTIARY } from "@/game/config";
import { PixelButton } from "./PixelButton";
import { TitleArt } from "./TitleArt";

export const TitleScreen = ({ initialName, initialDiff, initialMode = "side", player, onStart, onDaily, onLeaderboard, onAchievements, onGuide, onShop, loading }) => {
  const [name, setName] = useState(initialName);
  const [diff, setDiff] = useState(initialDiff);
  const [level, setLevel] = useState(1);
  const [mode, setMode] = useState(initialMode);
  const valid = name.trim().length > 0;
  const maxLevel = player && player.name.toLowerCase() === name.trim().toLowerCase() ? player.max_level : 1;

  return (
    <div className="title-screen" data-testid="title-screen">
      <div className="title-left fade-up">
        <p className="eyebrow">A rogue-like breakfast odyssey</p>
        <h1 className="game-title" data-testid="game-title-header">
          Toast <span>&amp;</span> Butter<br />Quest
        </h1>
        <p className="lede">
          Butter Boy needs toast. A lot of toast. Fight through 15 wild worlds (kitchens, jungles, beaches, mountains, volcanoes and beyond) armed with a humble butter knife, collect every slice, and finally get spread.
        </p>
        <form
          className="start-form"
          onSubmit={(e) => { e.preventDefault(); if (valid) onStart(name.trim(), diff, Math.min(level, maxLevel), mode); }}
        >
          <label className="field-label" htmlFor="player-name">Hero name</label>
          <input
            id="player-name"
            data-testid="player-name-input"
            className="pixel-input"
            maxLength={20}
            placeholder="Enter your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <span className="field-label">Game style</span>
          <div className="mode-grid" data-testid="mode-select">
            {[["side", "Side-Scroller", "Run, jump and stomp through 2D cartoon levels with bosses every 3 stages."], ["classic", "Classic Top-Down", "The original pixel-art dungeon crawler. Move in all directions."]].map(([k, label, desc]) => (
              <button type="button" key={k} data-testid={`mode-option-${k}`} className={`diff-card ${mode === k ? "active" : ""}`} onClick={() => setMode(k)}>
                <strong>{label}</strong>
                <small>{desc}</small>
              </button>
            ))}
          </div>
          <span className="field-label">Difficulty</span>
          <div className="diff-grid" data-testid="difficulty-select">
            {Object.entries(DIFFICULTIES).map(([key, d]) => (
              <button
                type="button"
                key={key}
                data-testid={`difficulty-option-${key}`}
                className={`diff-card ${diff === key ? "active" : ""} diff-${key}`}
                onClick={() => setDiff(key)}
              >
                <strong>{d.label}</strong>
                <small>{d.desc}</small>
              </button>
            ))}
          </div>
          {maxLevel > 1 && (
            <>
              <span className="field-label">Start at level (unlocked with gems)</span>
              <div className="level-select" data-testid="level-select">
                {Array.from({ length: maxLevel }, (_, i) => i + 1).map((l) => (
                  <button type="button" key={l} className={`level-pill ${level === l ? "active" : ""}`} onClick={() => setLevel(l)} data-testid={`level-option-${l}`}>{l}</button>
                ))}
              </div>
            </>
          )}
          <div className="title-actions">
            <PixelButton type="submit" disabled={!valid || loading} data-testid="start-game-button">
              <Play size={16} /> {loading ? "Loading..." : "Start Adventure"}
            </PixelButton>
            <PixelButton type="button" variant="ghost" onClick={() => onDaily(name.trim())} disabled={!valid || loading} data-testid="daily-challenge-button">
              <CalendarDays size={16} /> Daily Challenge{player?.daily_streak ? ` · ${player.daily_streak}-day streak` : ""}
            </PixelButton>
            <PixelButton type="button" variant="ghost" onClick={() => onShop(name.trim())} disabled={!valid} data-testid="open-shop-button">
              <Gem size={16} /> Gem Shop{player ? ` · ${player.gems}` : ""}
            </PixelButton>
            <PixelButton type="button" variant="ghost" onClick={onLeaderboard} data-testid="view-leaderboard-button">
              <Trophy size={16} /> Leaderboard
            </PixelButton>
            <PixelButton type="button" variant="ghost" onClick={onAchievements} disabled={!valid} data-testid="view-achievements-button">
              <Award size={16} /> Achievements
            </PixelButton>
            <PixelButton type="button" variant="ghost" onClick={() => onGuide(name.trim())} disabled={!valid} data-testid="view-enemy-guide-button">
              <BookOpen size={16} /> Enemy Guide{player ? ` · ${player.bestiary?.length || 0}/${BESTIARY.length}` : ""}
            </PixelButton>
          </div>
        </form>
        <p className="controls-hint" data-testid="controls-hint">
          <Keyboard size={14} /> Side-Scroller: A/D or ←/→ run · W / ↑ / Space jump · J / K / Click swing. Classic: WASD move · Space / J swing. P / Esc pauses
        </p>
      </div>
      <div className="title-right fade-up delay-2">
        <TitleArt />
      </div>
    </div>
  );
};
